/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { IObservation } from '@ahryman40k/ts-fhir-types/lib/R4';

/** Map HL7 nullFlavor to FHIR data-absent-reason codes */
function mapNullFlavorToDAR(code?: string | null): string | undefined {
  switch ((code || '').toUpperCase()) {
    case 'UNK':
      return 'unknown';
    case 'ASKU':
      return 'asked-unknown';
    case 'NASK':
      return 'not-asked';
    case 'NAV':
      return 'temp-unknown';
    case 'MSK':
      return 'masked';
    case 'NA':
      return 'not-applicable';
    case 'NP':
      return 'not-performed';
    default:
      return undefined;
  }
}

function setValueString(component: IObservation, s?: string | null) {
  if (s != null) (component as any).valueString = s;
}

function setValueInteger(component: IObservation, n: number) {
  (component as any).valueInteger = n;
}

function setValueQuantity(
  component: IObservation,
  n: number,
  unit?: string | null,
  comparator?: '<' | '<=' | '>=' | '>'
) {
  (component as any).valueQuantity = {
    value: n,
    unit: unit ?? undefined,
    ...(comparator ? { comparator } : {}),
  };
}

function toNumberOrUndefined(raw?: string | null, unit?: string | null): number | undefined {
  if (!raw) return undefined;
  let s = raw.trim();
  if (!s) return undefined;

  // Normalize unicode minus and obvious thousands separators (e.g., "1,234")
  s = s.replace(/\u2212/g, '-');

  // Heuristic for decimal comma vs thousands:
  // - If string has exactly one comma and no dot, treat comma as decimal point.
  // - Otherwise, strip commas as thousands sep.
  if (s.includes(',') && !s.includes('.')) {
    const onlyOneComma = (s.match(/,/g) || []).length === 1;
    if (onlyOneComma && /\d,\d/.test(s)) {
      s = s.replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else {
    s = s.replace(/,/g, '');
  }

  // Strip relational symbols like "<", ">", "≤", "≥" (value comparator handled separately)
  s = s.replace(/^[<>]=?\s*/, '');

  // If percent either in the unit or value, strip the % sign
  if (/%/.test(s) || (unit && unit.includes('%'))) s = s.replace(/%/g, '');

  // Extract the first numeric token (incl. scientific notation)
  const m = s.match(/[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/);
  if (!m) return undefined;

  const n = Number(m[0]);
  return Number.isFinite(n) ? n : undefined;
}

function toIntOrUndefined(raw?: string | null): number | undefined {
  if (!raw) return undefined;
  const m = raw.trim().match(/^[+-]?\d+$/);
  if (!m) return undefined;
  const n = Number(m[0]);
  return Number.isSafeInteger(n) ? n : undefined;
}

function setQuantityIfNumber(
  component: IObservation,
  raw: string | null | undefined,
  unit: string | null | undefined,
  comparator?: '<' | '<=' | '>=' | '>'
) {
  const n = toNumberOrUndefined(raw, unit);
  if (n !== undefined) {
    setValueQuantity(component, n, unit, comparator);
    return true;
  }
  return false;
}

function setRangeIfNumbers(
  component: IObservation,
  lowRaw?: string | null,
  lowUnit?: string | null,
  highRaw?: string | null,
  highUnit?: string | null
) {
  const low = toNumberOrUndefined(lowRaw, lowUnit);
  const high = toNumberOrUndefined(highRaw, highUnit);
  if (low === undefined && high === undefined) return false;

  (component as any).valueRange = {
    ...(low !== undefined ? { low: { value: low, unit: lowUnit ?? undefined } } : {}),
    ...(high !== undefined ? { high: { value: high, unit: highUnit ?? undefined } } : {}),
  };
  return true;
}

/**
 * Parses HL7-style date/time strings into JavaScript Date objects.
 * Supports both with and without timezone offsets.
 *
 * Examples:
 *   "20250311205600+0000" → Tue Mar 11 2025 20:56:00 UTC
 *   "20250311205600"      → Local time equivalent of 2025-03-11 20:56:00
 */
export function parseHL7Date(input: string): Date | undefined {
  const withTZ = input.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})([+-]\d{4})$/);
  if (withTZ) {
    const [, year, month, day, hour, minute, second] = withTZ;
    const offset = withTZ[7]!; // guaranteed by the regex
    const iso = `${year}-${month}-${day}T${hour}:${minute}:${second}${offset.slice(0, 3)}:${offset.slice(3)}`;
    return new Date(iso);
  }

  const noTZ = input.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/);
  if (noTZ) {
    const [, year, month, day, hour, minute, second] = noTZ;
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  }

  return undefined;
}

export function parseUSDateTimeString(str: string): Date | undefined {
  // Example: "04/01/2025 10:46 AM PDT"
  const parsed = Date.parse(str);
  return isNaN(parsed) ? undefined : new Date(parsed);
}

function getNormalizedXsiType(valueEl: Element | null | undefined): string | undefined {
  if (!valueEl) return undefined;
  const xsiNs = 'http://www.w3.org/2001/XMLSchema-instance';
  let t =
    valueEl.getAttributeNS(xsiNs, 'type') ||
    valueEl.getAttribute('xsi:type') ||
    valueEl.getAttribute('type') || // some feeds are sloppy
    undefined;
  if (!t) return undefined;
  const colon = t.indexOf(':');
  return colon >= 0 ? t.slice(colon + 1) : t;
}

function getComparatorFromOperator(op?: string | null): '<' | '<=' | '>=' | '>' | undefined {
  switch ((op || '').toUpperCase()) {
    case 'LT':
      return '<';
    case 'LE':
      return '<=';
    case 'GE':
      return '>=';
    case 'GT':
      return '>';
    default:
      return undefined;
  }
}

/** Take CCDA xml Observation and update the given component values.
 *  The obs is expected to be the xml from:
 *    const obs = doc.querySelector(component > observation text > reference[value="#${component.id}"])?.parentElement?.parentElement;
 *  E.g.
 *    <observation classCode="OBS" moodCode="EVN"> <templateId root="2.16.840.1.113883.10.20.22.4.27" extension="2014-06-09" />
 *     <id root="fdbd831b-5919-4f06-9467-76b07022f8e8" />
 *     <code code="8480-6" codeSystem="2.16.840.1.113883.6.1" codeSystemName="LOINC" displayName="Systolic blood pressure" />
 *     <text> <!-- This reference identifies content in human readable formatted text--> <reference value="#SystolicBP_1"/> </text> <statusCode code="completed" />
 *     <effectiveTime value="20140520193605-0500" />
 *     <!-- Example of Value with UCUM unit. Note that metric units used in this example--> <value xsi:type="PQ" value="120" unit="mm[Hg]" />
 *     <!-- Additional information of interpretation and/or reference range may be included but are optional-->
 *    </observation>
 */
export function xmlToIObservation(obs: HTMLElement, component: IObservation) {
  // ----- code / display / text -----
  const loinc = obs.querySelector('code[codeSystem="2.16.840.1.113883.6.1"]')?.getAttribute('code');
  const displayName =
    obs.querySelector('code > translation')?.getAttribute('displayName') ||
    obs.querySelector('code')?.getAttribute('displayName');

  component.code = {
    coding: [
      {
        system: 'http://loinc.org',
        code: loinc || '',
        display: displayName || undefined,
      },
    ],
    text: obs.querySelector('code > originalText')?.textContent?.trim() || 'Unnamed Observation',
  };

  // ----- interpretation -----
  const interpretationCode = obs.querySelector(':scope > interpretationCode')?.getAttribute('code');
  const interpretation = obs.querySelector(':scope > interpretationCode originalText')?.textContent?.trim();
  if (interpretationCode) {
    component.interpretation = [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
            code: interpretationCode,
          },
        ],
        text: interpretation || interpretationCode,
      },
    ];
  }

  // ----- effective[x] -----
  const effEl = obs.querySelector(':scope > effectiveTime');
  if (effEl) {
    const effValue = effEl.getAttribute('value');
    const low = effEl.querySelector(':scope > low')?.getAttribute('value');
    const high = effEl.querySelector(':scope > high')?.getAttribute('value');
    if (low || high) {
      const start = low ? parseHL7Date(low)?.toISOString() : undefined;
      const end = high ? parseHL7Date(high)?.toISOString() : undefined;
      if (start || end) {
        (component as any).effectivePeriod = {
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
        };
      }
    } else if (effValue) {
      const dt = parseHL7Date(effValue)?.toISOString();
      if (dt) component.effectiveDateTime = dt;
    }
  }

  // ----- dataAbsentReason (nullFlavor / xsi:nil) -----
  const value = obs.querySelector(':scope > value');
  const obsNullFlavor = obs.getAttribute('nullFlavor');
  const valueNullFlavor = value?.getAttribute('nullFlavor');
  const valueXsiNil =
    value?.getAttributeNS('http://www.w3.org/2001/XMLSchema-instance', 'nil') || value?.getAttribute('xsi:nil');

  const dar = mapNullFlavorToDAR(valueNullFlavor || obsNullFlavor);
  if ((valueXsiNil && /^(true|1)$/i.test(valueXsiNil)) || dar) {
    (component as any).dataAbsentReason = dar
      ? { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason', code: dar }] }
      : { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason', code: 'unknown' }] };
    return; // value absent → stop here
  }

  // ----- value[x] dispatch -----
  const units = value?.getAttribute('unit');
  const valueStr = value?.getAttribute('value');
  const xsiType = getNormalizedXsiType(value);
  const comparator = getComparatorFromOperator(value?.getAttribute('operator'));

  // Note the value will have a xsi:type which means the type:
  // | xsi:type | Description | Example |
  // | ---------- | ----------------------------- | ---------------------------------- |
  // | ST | String/Text | "Positive" |
  // | REAL | Floating point number | 5.0 |
  // | INT | Integer | 3 |
  // | PQ | Physical quantity (w/ unit) | 4.5% |
  // | TS | Timestamp (date/time) | 20230101 |
  // | CD | Coded data (with code+system) | code="1234" codeSystem="2.16..." |
  // | IVL_TS | Interval of time | Start/End time range |
  // | IVL_REAL | Interval of real numbers | low="1.0" high="10.0" |
  switch (xsiType) {
    case 'PQ':
    case 'REAL': {
      // Try numeric; if not, fall back to string
      if (!setQuantityIfNumber(component, valueStr, units, comparator)) {
        if (valueStr?.trim()) setValueString(component, valueStr.trim());
      }
      break;
    }
    case 'ST': {
      setValueString(component, valueStr || '');
      break;
    }
    case 'INT': {
      const n = toIntOrUndefined(valueStr);
      if (n !== undefined) setValueInteger(component, n);
      else if (valueStr?.trim()) setValueString(component, valueStr.trim());
      break;
    }
    case 'CD': {
      (component as any).valueCodeableConcept = {
        coding: [
          {
            code: value?.getAttribute('code') || '',
            system: value?.getAttribute('codeSystem') || '',
            ...(value?.getAttribute('displayName') ? { display: value.getAttribute('displayName')! } : {}),
          },
        ],
        ...(value?.querySelector('originalText')?.textContent?.trim()
          ? { text: value.querySelector('originalText')!.textContent!.trim() }
          : {}),
      };
      break;
    }
    case 'IVL_REAL': {
      const low = value?.querySelector(':scope > low');
      const high = value?.querySelector(':scope > high');
      const wroteRange = setRangeIfNumbers(
        component,
        low?.getAttribute('value'),
        low?.getAttribute('unit'),
        high?.getAttribute('value'),
        high?.getAttribute('unit')
      );
      if (!wroteRange) {
        // Last resort: some feeds put a single @value on <value>
        if (!setQuantityIfNumber(component, valueStr, units, comparator) && valueStr?.trim()) {
          setValueString(component, valueStr.trim());
        }
      }
      break;
    }
    case undefined:
    default: {
      // Unknown/missing type: best-effort numeric, else string. Warn only when we end up with neither.
      const wrote = setQuantityIfNumber(component, valueStr, units, comparator);
      if (!wrote) {
        if (valueStr?.trim()) {
          setValueString(component, valueStr.trim());
        } else {
          console.warn(`Unhandled xsi:type "${xsiType}" for observation ${(component as any).id}.`);
        }
      }
      if (xsiType !== undefined) {
        console.warn(`Unhandled xsi:type "${xsiType}" for observation ${(component as any).id}. Value: ${valueStr}`);
      }
      break;
    }
  }
}
